import {
  CanvasTexture,
  LinearFilter,
  RepeatWrapping,
  SRGBColorSpace,
  type Texture,
} from 'three'
import { Rng } from '@/core/Rng'

/**
 * All surface detail is generated once at startup into offscreen canvases.
 *
 * Value-noise fBm is enough here: the art direction is grimy concrete and rust,
 * where the eye reads large-scale blotchiness and fine grain, not structure.
 * Generating textures rather than shipping them keeps the build asset-free and
 * lets every surface be re-tinted per arena for free.
 */

interface NoiseField {
  sample(x: number, y: number): number
}

/** Tileable value noise on a `size × size` lattice. */
function valueNoise(size: number, rng: Rng): NoiseField {
  const data = new Float32Array(size * size)
  for (let i = 0; i < data.length; i++) data[i] = rng.next()

  return {
    sample(x: number, y: number): number {
      const fx = x * size
      const fy = y * size
      const x0 = Math.floor(fx)
      const y0 = Math.floor(fy)
      const tx = fx - x0
      const ty = fy - y0
      const sx = tx * tx * (3 - 2 * tx)
      const sy = ty * ty * (3 - 2 * ty)

      const i0 = ((y0 % size) + size) % size
      const i1 = (i0 + 1) % size
      const j0 = ((x0 % size) + size) % size
      const j1 = (j0 + 1) % size

      const a = data[i0 * size + j0]
      const b = data[i0 * size + j1]
      const c = data[i1 * size + j0]
      const d = data[i1 * size + j1]

      const top = a + (b - a) * sx
      const bottom = c + (d - c) * sx
      return top + (bottom - top) * sy
    },
  }
}

function fbm(fields: NoiseField[], x: number, y: number, octaves: number): number {
  let sum = 0
  let amp = 1
  let norm = 0
  let freq = 1
  for (let o = 0; o < octaves; o++) {
    sum += fields[o % fields.length].sample(x * freq, y * freq) * amp
    norm += amp
    amp *= 0.5
    freq *= 2
  }
  return sum / norm
}

function canvas(size: number): { ctx: CanvasRenderingContext2D; image: ImageData } {
  const el = document.createElement('canvas')
  el.width = size
  el.height = size
  const ctx = el.getContext('2d')!
  return { ctx, image: ctx.createImageData(size, size) }
}

function finish(
  ctx: CanvasRenderingContext2D,
  image: ImageData,
  repeat: number,
  srgb: boolean,
): CanvasTexture {
  ctx.putImageData(image, 0, 0)
  const tex = new CanvasTexture(ctx.canvas)
  tex.wrapS = RepeatWrapping
  tex.wrapT = RepeatWrapping
  tex.repeat.set(repeat, repeat)
  tex.minFilter = LinearFilter
  tex.magFilter = LinearFilter
  tex.anisotropy = 4
  if (srgb) tex.colorSpace = SRGBColorSpace
  tex.needsUpdate = true
  return tex
}

function rgb(hex: number): [number, number, number] {
  return [(hex >> 16) & 255, (hex >> 8) & 255, hex & 255]
}

export interface ConcreteOptions {
  size?: number
  repeat?: number
  seed?: number
  /** Strength of the dark blotches, 0..1. */
  grime?: number
  /** Strength of the rust bleed, 0..1. */
  rust?: number
}

/** Pitted, stained concrete — the material every platform is made of. */
export function makeConcreteTexture(tint: number, options: ConcreteOptions = {}): Texture {
  const size = options.size ?? 512
  const seed = options.seed ?? 1234
  const grime = options.grime ?? 0.55
  const rustAmount = options.rust ?? 0.25
  const rng = new Rng(seed)
  const fields = [valueNoise(8, rng), valueNoise(16, rng), valueNoise(64, rng)]
  const { ctx, image } = canvas(size)
  const [br, bg, bb] = rgb(tint)
  const [rr, rg_, rb] = rgb(0x7a3b1e)

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size
      const v = y / size
      const base = fbm(fields, u, v, 5)
      const fine = fields[2].sample(u * 4, v * 4)

      // Large blotches darken, fine grain adds the sandy speckle of concrete.
      let shade = 0.72 + base * 0.5 * (1 - grime) + fine * 0.16
      shade *= 1 - grime * Math.pow(1 - base, 2.2)

      const rustMask = Math.pow(Math.max(0, base - 0.55) * 2.2, 2) * rustAmount
      const i = (y * size + x) * 4
      image.data[i] = clamp255(br * shade * (1 - rustMask) + rr * rustMask)
      image.data[i + 1] = clamp255(bg * shade * (1 - rustMask) + rg_ * rustMask)
      image.data[i + 2] = clamp255(bb * shade * (1 - rustMask) + rb * rustMask)
      image.data[i + 3] = 255
    }
  }
  return finish(ctx, image, options.repeat ?? 4, true)
}

/** Corroded steel for barrels, car wrecks and guard rails. */
export function makeRustTexture(base: number, seed = 77): Texture {
  const size = 256
  const rng = new Rng(seed)
  const fields = [valueNoise(6, rng), valueNoise(24, rng), valueNoise(96, rng)]
  const { ctx, image } = canvas(size)
  const [br, bg, bb] = rgb(base)
  const [rr, rg_, rb] = rgb(0xa8552b)
  const [dr, dg, db] = rgb(0x2a1a12)

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size
      const v = y / size
      const n = fbm(fields, u, v, 4)
      // Rust eats the metal in patches, with dark pitting inside the worst of them.
      const rustMask = Math.pow(Math.max(0, n - 0.42) * 1.9, 1.4)
      const pit = Math.pow(Math.max(0, n - 0.78) * 4, 2)
      const shade = 0.75 + n * 0.4

      const i = (y * size + x) * 4
      image.data[i] = clamp255(mix3(br * shade, rr, rustMask, dr, pit))
      image.data[i + 1] = clamp255(mix3(bg * shade, rg_, rustMask, dg, pit))
      image.data[i + 2] = clamp255(mix3(bb * shade, rb, rustMask, db, pit))
      image.data[i + 3] = 255
    }
  }
  return finish(ctx, image, 1, true)
}

/** Vertical fibrous bark for the dead trees. */
export function makeBarkTexture(seed = 909): Texture {
  const size = 256
  const rng = new Rng(seed)
  const fields = [valueNoise(4, rng), valueNoise(32, rng), valueNoise(128, rng)]
  const { ctx, image } = canvas(size)

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size
      const v = y / size
      // Stretching the sample along V turns isotropic noise into wood fibre.
      const n = fbm(fields, u, v * 0.18, 4)
      const ridge = Math.abs(n - 0.5) * 2
      const shade = 0.35 + ridge * 0.55
      const i = (y * size + x) * 4
      image.data[i] = clamp255(74 * shade + 12)
      image.data[i + 1] = clamp255(66 * shade + 12)
      image.data[i + 2] = clamp255(52 * shade + 10)
      image.data[i + 3] = 255
    }
  }
  return finish(ctx, image, 1, true)
}

/** Grayscale fBm used as a roughness map wherever a surface needs variation. */
export function makeRoughnessTexture(seed = 4242, contrast = 0.6): Texture {
  const size = 256
  const rng = new Rng(seed)
  const fields = [valueNoise(8, rng), valueNoise(32, rng), valueNoise(96, rng)]
  const { ctx, image } = canvas(size)

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const n = fbm(fields, x / size, y / size, 4)
      const v = clamp255(255 * (0.5 + (n - 0.5) * contrast * 2))
      const i = (y * size + x) * 4
      image.data[i] = v
      image.data[i + 1] = v
      image.data[i + 2] = v
      image.data[i + 3] = 255
    }
  }
  return finish(ctx, image, 2, false)
}

/** Soft radial falloff — ground glow, shadow blobs, particle sprites. */
export function makeRadialTexture(inner = 1, outer = 0, power = 2): Texture {
  const size = 128
  const { ctx, image } = canvas(size)
  const half = size / 2

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - half) / half
      const dy = (y - half) / half
      const d = Math.min(1, Math.hypot(dx, dy))
      const a = Math.pow(1 - d, power)
      const value = inner * a + outer * (1 - a)
      const i = (y * size + x) * 4
      image.data[i] = 255
      image.data[i + 1] = 255
      image.data[i + 2] = 255
      image.data[i + 3] = clamp255(value * 255)
    }
  }
  return finish(ctx, image, 1, false)
}

function mix3(
  base: number,
  layer1: number,
  amount1: number,
  layer2: number,
  amount2: number,
): number {
  const a = base * (1 - amount1) + layer1 * amount1
  return a * (1 - amount2) + layer2 * amount2
}

function clamp255(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v | 0
}
